import React from "react";

import { SerialPicker } from "../../navigation/v2/store/warehouses/traslados";
import { DeliveryPlan } from "../../services/v2/inventory.service";
import { FIELD_FONT } from "./kitUi";

/** articleId elegido y cantidad (texto tal cual se escribe; se valida al salir del campo y al guardar), por línea del kit. */
export type PiecePicks = Record<string, { articleId: number; qty: string; serials?: string[] }>;

const key = (line: any) => String(line.componentId ?? "");
const qtyOf = (text: string): number => /^\d+$/.test(text) ? parseInt(text, 10) : NaN;

export const pendingLinesOf = (plan: DeliveryPlan | null) => (plan ? plan.lines.filter((line) => line.pending > 0) : []);

/** What the student asked for when buying comes already picked (only if it is here to hand out). */
export const presetPicks = (plan: DeliveryPlan): PiecePicks => {
	const preset: PiecePicks = {};
	(plan.lines || []).forEach((line: any) => {
		const option = line.pending > 0 && line.preferredArticleId ? (line.options || []).find((candidate: any) => candidate.articleId === line.preferredArticleId) : null;
		if (option && option.onHand > 0 && !option.serialized) preset[key(line)] = { articleId: option.articleId, qty: String(Math.min(line.pending, option.onHand)), serials: [] };
	});
	return preset;
};

const qtyOkFor = (plan: DeliveryPlan, picked: PiecePicks, componentKey: string): boolean => {
	const quantity = qtyOf(picked[componentKey]?.qty || "");
	const line = pendingLinesOf(plan).find((candidate) => key(candidate) === componentKey);
	return !!line && quantity >= 1 && quantity <= line.pending;
};

/** At least one piece picked and every quantity between 1 and what is missing. */
export const piecesValid = (plan: DeliveryPlan | null, picked: PiecePicks): boolean => {
	if (!plan) return false;
	const chosen = Object.keys(picked).filter((componentKey) => !!picked[componentKey]);
	return chosen.length > 0 && chosen.every((componentKey) => qtyOkFor(plan, picked, componentKey));
};

/** The "items" the API expects for the picked pieces. */
export const piecesItems = (picked: PiecePicks) => Object.keys(picked).filter((componentKey) => !!picked[componentKey]).map((componentKey) => ({
	componentId: componentKey === "" ? null : Number(componentKey),
	articleId: picked[componentKey].articleId,
	quantity: qtyOf(picked[componentKey].qty),
	serials: picked[componentKey].serials || []
}));

export interface PiecesPickerProps {
	plan: DeliveryPlan;
	picked: PiecePicks;
	onChange: (picked: PiecePicks) => void;
	busy?: boolean;
}

/**
 * Entrega pieza por pieza (catálogo nuevo): por cada línea que falta, qué variante o sustituto sale, cuántas y con qué número
 * de serie. Lo usan "Registrar Entrega" (un producto) y "Entregar Factura" (varios productos con una sola firma).
 */
const PiecesPicker: React.FC<PiecesPickerProps> = ({ plan, picked, onChange, busy }) => {
	const pendingLines = pendingLinesOf(plan);

	const setPick = (line: any, articleId: number | null) => {
		const next = { ...picked };
		if (articleId === null) delete next[key(line)];
		else next[key(line)] = { articleId, qty: picked[key(line)]?.qty || "1", serials: [] };
		onChange(next);
	};
	const setQty = (line: any, text: string) => {
		const current = picked[key(line)];
		if (!current) return;
		onChange({ ...picked, [key(line)]: { ...current, qty: text.replace(/\D/g, "").slice(0, 4) } });
	};
	/** Al salir del campo: vacío o 0 → 1; más de lo que falta → lo que falta. */
	const fixQty = (line: any) => {
		const current = picked[key(line)];
		if (!current) return;
		const quantity = qtyOf(current.qty);
		const fixed = isNaN(quantity) || quantity < 1 ? 1 : Math.min(quantity, line.pending);
		if (String(fixed) !== current.qty) onChange({ ...picked, [key(line)]: { ...current, qty: String(fixed) } });
	};

	if (pendingLines.length < 1) return <div style={{ fontSize: "13px", color: "var(--wa-verde)", padding: "8px 0" }}>Ya se entregó todo lo que compone este kit.</div>;

	return <>{pendingLines.map((line) => {
		const current = picked[key(line)];
		const quantity = current ? qtyOf(current.qty) : NaN;
		const qtyOk = qtyOkFor(plan, picked, key(line));
		return <div key={key(line)} style={{ border: "1px solid var(--wa-filo)", borderRadius: "8px", padding: "8px 10px", marginBottom: "8px" }}>
			<div style={{ display: "flex", justifyContent: "space-between", gap: "8px", flexWrap: "wrap" }}>
				<span style={{ fontWeight: 600, fontSize: "14px" }}>{line.name}
					{(() => {
						const asked = line.preferredArticleId ? line.options.find((choiceOption) => choiceOption.articleId === line.preferredArticleId) : null;
						return asked ? <span style={{ fontWeight: 400, fontSize: "12px", color: "var(--wa-apagado)" }}> · pidió {asked.label || asked.sizeValue || asked.name}</span> : null;
					})()}
				</span>
				<span style={{ fontSize: "12px", color: "var(--wa-apagado)" }}>faltan {line.pending} de {line.needed}{line.optional ? " · opcional" : ""}</span>
			</div>
			<div style={{ display: "flex", gap: "8px", alignItems: "center", marginTop: "6px", flexWrap: "wrap" }}>
				<select className="form-select" style={{ height: "34px", fontSize: FIELD_FONT, flex: "1 1 220px", minWidth: 0 }} disabled={busy}
					value={current ? String(current.articleId) : ""}
					onChange={(event) => setPick(line, event.target.value === "" ? null : Number(event.target.value))}>
					<option value="">No entregar ahora</option>
					{line.options.map((choiceOption) => <option key={choiceOption.articleId} value={choiceOption.articleId} disabled={choiceOption.onHand < 1}>
						{choiceOption.label && !choiceOption.isSubstitute ? (choiceOption.colorValue ? choiceOption.label : "Talla " + choiceOption.label) : choiceOption.sizeValue && !choiceOption.isSubstitute ? "Talla " + choiceOption.sizeValue : choiceOption.name}{choiceOption.isSubstitute ? " (sustituto)" : ""} · hay {choiceOption.onHand}{choiceOption.inTransit ? " · " + choiceOption.inTransit + " en camino" + (choiceOption.eta ? " (" + choiceOption.eta.slice(0, 5) + ")" : "") : ""}
					</option>)}
				</select>
				{current ? <input type="text" inputMode="numeric" pattern="[0-9]*" className="form-control" disabled={busy} aria-label="Cantidad"
					style={{ height: "34px", fontSize: FIELD_FONT, width: "80px", borderColor: qtyOk ? undefined : "var(--wa-oro)" }}
					value={current.qty} onChange={(event) => setQty(line, event.target.value)} onBlur={() => fixQty(line)} /> : null}
			</div>
			{current && !qtyOk ? <div style={{ fontSize: "12px", color: "var(--wa-oro)", marginTop: "4px" }}>La cantidad va de 1 a {line.pending}.</div> : null}
			<div>
				{(() => {
					const chosenOption: any = current ? line.options.find((choiceOption: any) => choiceOption.articleId === current.articleId) : null;
					if (!chosenOption || !chosenOption.serialized) return null;
					return <SerialPicker options={chosenOption.serials || []} value={current.serials || []} max={isNaN(quantity) ? 0 : quantity} busy={busy}
						onChange={(serials) => onChange({ ...picked, [key(line)]: { ...current, serials } })} />;
				})()}
			</div>
			{line.options.every((choiceOption) => choiceOption.onHand < 1)
				? <div style={{ fontSize: "12px", color: "var(--wa-oro)", marginTop: "4px" }}>
					{(() => {
						if (line.options.length < 1) return "Este producto no tiene piezas o tallas que se puedan entregar. Revísalo en Productos.";
						const coming = line.options.filter((choiceOption) => (choiceOption.inTransit || 0) > 0);
						if (coming.length < 1) return "No hay existencia en esta bodega. Hay que pedirlo en Compras.";
						const units = coming.reduce((sum, choiceOption) => sum + (choiceOption.inTransit || 0), 0);
						const eta = coming.map((choiceOption) => choiceOption.eta).filter(Boolean).sort()[0];
						return "No hay existencia en esta bodega. Vienen " + units + " en camino" + (eta ? ", llegan el " + eta : "") + ".";
					})()}
				</div> : null}
		</div>;
	})}</>;
};

export default PiecesPicker;
